import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useFacilityStore } from '../store/facilityStore'
import { apiFetch } from '../lib/apiFetch'

// Room-type → short label shown on cards
const ROOM_TYPE_LABEL = {
  flowering:       'FLR',
  veg:             'VEG',
  'pre-veg':       'PVG',
  drying:          'DRY',
  trimming:        'TRM',
  extraction:      'EXT',
  packaging:       'PKG',
  storage:         'STR',
  'secure-storage':'SEC',
}

// Colour dot by room type family
const ROOM_TYPE_DOT = {
  flowering:       '#f472b6',
  veg:             '#4ade80',
  'pre-veg':       '#86efac',
  drying:          '#fbbf24',
  trimming:        '#fb923c',
  extraction:      '#a78bfa',
  packaging:       '#60a5fa',
  storage:         '#94a3b8',
  'secure-storage':'#94a3b8',
}

function getMonday(date) {
  const d = new Date(date)
  const day = d.getDay()
  const diff = day === 0 ? -6 : 1 - day
  d.setDate(d.getDate() + diff)
  d.setHours(0, 0, 0, 0)
  return d
}

function addDays(date, n) {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
}

export default function CalendarCarousel() {
  const lcCalendarTasks   = useFacilityStore(s => s.lcCalendarTasks)
  const lcCalendarLoading = useFacilityStore(s => s.lcCalendarLoading)
  const lcRoomMap         = useFacilityStore(s => s.lcRoomMap)
  const addTask           = useFacilityStore(s => s.addTask)
  const setHoveredRoomId  = useFacilityStore(s => s.setHoveredRoomId)

  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()))
  const [boardedIds, setBoardedIds] = useState(() => new Set())
  const [sendError, setSendError]   = useState(null)

  // ── Resize state ─────────────────────────────────────────────────
  // HEADER_H: day-label row height (px) — label + padding
  const HEADER_H = 26
  // CARD_H: height of one task card + gap (px)
  const CARD_H = 26
  // MIN_CARDS / MAX_CARDS (max is just a large number; let CSS handle real max)
  const MIN_CARDS = 1
  const PADDING_V = 10  // top + bottom padding inside .carousel-day-tasks

  const [numCards, setNumCards] = useState(() => {
    const saved = parseInt(localStorage.getItem('mtl-carousel-cards') ?? '', 10)
    return Number.isFinite(saved) && saved >= MIN_CARDS ? saved : 4
  })

  // Derive pixel height from numCards
  const carouselH = HEADER_H + PADDING_V + numCards * CARD_H

  // Publish --carousel-h for any consumer (toolbox clamping)
  useEffect(() => {
    const topbarH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--topbar-h') || '52', 10)
    document.documentElement.style.setProperty('--carousel-h', `${topbarH + carouselH}px`)
    return () => {}
  }, [carouselH])

  // Resize drag handle logic
  const resizeDragging = useRef(false)
  const resizeStartY   = useRef(0)
  const resizeStartN   = useRef(numCards)

  const onResizeStart = useCallback((e) => {
    e.preventDefault()
    resizeDragging.current = true
    resizeStartY.current   = e.clientY
    resizeStartN.current   = numCards
    document.body.style.cursor = 'ns-resize'

    const onMove = (me) => {
      if (!resizeDragging.current) return
      const delta = me.clientY - resizeStartY.current
      const cardsDelta = Math.round(delta / CARD_H)
      const next = Math.max(MIN_CARDS, resizeStartN.current + cardsDelta)
      setNumCards(next)
    }
    const onUp = () => {
      resizeDragging.current = false
      document.body.style.cursor = ''
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      localStorage.setItem('mtl-carousel-cards', String(numCards))
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [numCards])

  // Persist on numCards change
  useEffect(() => {
    localStorage.setItem('mtl-carousel-cards', String(numCards))
  }, [numCards])


  const weekDays = useMemo(() => (
    Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  ), [weekStart])

  // Build mcRoomId lookup: lcRoomId → mcRoomId (reverse the map)
  const lcToMcRoomId = useMemo(() => {
    const map = {}
    for (const [mcId, lcId] of Object.entries(lcRoomMap)) map[lcId] = mcId
    return map
  }, [lcRoomMap])

  const tasksByDay = useMemo(() => {
    const byDay = weekDays.map(() => [])
    const weekStart = weekDays[0]?.toISOString().slice(0, 10)
    const weekEnd   = weekDays[6]?.toISOString().slice(0, 10)
    console.log('[Calendar] total lcCalendarTasks:', lcCalendarTasks.length,
      '| week:', weekStart, '→', weekEnd,
      '| boardedIds:', boardedIds.size)
    lcCalendarTasks.forEach(task => {
      if (task.completed || boardedIds.has(task.id) || !task.dueDate) return
      const due = new Date(task.dueDate + 'T00:00:00')
      const idx = weekDays.findIndex(d => isSameDay(d, due))
      if (idx !== -1) byDay[idx].push(task)
    })
    const total = byDay.reduce((s, d) => s + d.length, 0)
    console.log('[Calendar] tasks in this week:', total)
    // Sort by room name within each day for consistency
    byDay.forEach(day => day.sort((a, b) => (a.lcRoomName ?? '').localeCompare(b.lcRoomName ?? '')))
    return byDay
  }, [lcCalendarTasks, weekDays, boardedIds])

  // → Board: create an MC whiteboard task from the LC cultivation task
  async function sendToBoard(lcTask) {
    setSendError(null)
    setBoardedIds(prev => new Set([...prev, lcTask.id]))
    const mcRoomId = lcToMcRoomId[lcTask.lcRoomId] ?? null
    const title = lcTask.name ?? `${lcTask.scheduleName} — ${lcTask.lcRoomName}`
    const newTask = {
      title,
      status:   'todo',
      priority: 'normal',
      roomId:   mcRoomId,
      dueDate:  lcTask.dueDate,
    }
    try {
      console.log('[Calendar] sending to board:', newTask)
      const saved = await apiFetch('/api/tasks', {
        method: 'POST',
        body:   JSON.stringify(newTask),
      })
      console.log('[Calendar] board task created:', saved)
      addTask(saved)
    } catch (err) {
      console.error('[Calendar] sendToBoard failed:', err)
      setSendError(err?.message ?? 'Failed to add task')
      // Revert optimistic removal
      setBoardedIds(prev => { const s = new Set(prev); s.delete(lcTask.id); return s })
    }
  }

  return (
    <div className="calendar-carousel" style={{ height: carouselH }}>
      <button
        className="carousel-nav-btn"
        onClick={() => setWeekStart(d => addDays(d, -7))}
        aria-label="Previous week"
      >‹</button>

      {sendError && (
        <div className="carousel-send-error" onClick={() => setSendError(null)}>
          ⚠ {sendError}
        </div>
      )}

      <div className="carousel-days">
        {weekDays.map((day, di) => {
          const dayTasks = tasksByDay[di]
          const label = day.toLocaleDateString('en-CA', { weekday: 'short', day: 'numeric' })
          // Visible tasks capped to numCards so nothing is half-cut
          const visibleTasks = dayTasks.slice(0, numCards)
          return (
            <div key={di} className="carousel-day">
              <div className="carousel-day-label">{label}</div>
              <div
                className="carousel-day-tasks"
                style={{ height: numCards * CARD_H, overflow: 'hidden', flex: 'none' }}
              >
                {lcCalendarLoading ? (
                  Array.from({ length: Math.min(numCards, 3) }, (_, i) => (
                    <div key={i} className="carousel-skeleton-card" />
                  ))
                ) : visibleTasks.length === 0 ? (
                  <div className="carousel-day-empty" />
                ) : (
                  visibleTasks.map(task => {
                    const mcRoomId = lcToMcRoomId[task.lcRoomId] ?? null
                    const dot = ROOM_TYPE_DOT[task.roomType] ?? '#60a5fa'
                    const badge = ROOM_TYPE_LABEL[task.roomType] ?? task.roomType?.slice(0, 3).toUpperCase()
                    return (
                      <div
                        key={task.id}
                        className="carousel-task-card"
                        onMouseEnter={() => setHoveredRoomId(mcRoomId)}
                        onMouseLeave={() => setHoveredRoomId(null)}
                      >
                        <span
                          className="carousel-task-dot"
                          style={{ background: dot }}
                        />
                        <div className="carousel-task-info">
                          <span className="carousel-task-title">{task.name}</span>
                          <span className="carousel-task-room">
                            {badge} · {task.lcRoomName}
                          </span>
                        </div>
                        <button
                          className="carousel-to-board-btn"
                          onClick={() => sendToBoard(task)}
                          title="Add to whiteboard"
                        >→</button>
                      </div>
                    )
                  })
                )}
              </div>
            </div>
          )
        })}
      </div>

      <button
        className="carousel-nav-btn"
        onClick={() => setWeekStart(d => addDays(d, 7))}
        aria-label="Next week"
      >›</button>

      {/* Resize handle — drag up/down to change number of visible cards */}
      <div
        className="carousel-resize-handle"
        onMouseDown={onResizeStart}
        title="Drag to resize"
      />
    </div>
  )
}

